import React from 'react'
import { useFormattedPrice } from 'src/sdk/product/useFormattedPrice'
import Price from 'src/components/ui/Price'
import { Installment } from 'src/components/ui/Installment'
import BuyButton from 'src/components/ui/BuyButton'
import ReleaseTag from 'src/components/common/ReleaseTag/ReleaseTag'

import type { SKUPriceRange } from './utils/getSKUPriceRange'

type HandlePriceType = {
  spotPrice: number
  listPrice: number
  isProductAvailable: boolean
  price: number
  shelfClass: string
  viewInstallments?: boolean
  isVivaraYou?: boolean
  hasPolarizedLens?: boolean
  collectionId?: {
    id: string | null
    name: string | null
  }
  skuPriceRange?: SKUPriceRange | null
}

const renderTagVivaraYouOrInstallments = ({
  isVivaraYou,
  collectionId,
  shelfClass,
  isProductAvailable,
  price,
  spotPrice,
  viewInstallments,
  skuPriceRange = null,
}: Omit<HandlePriceType, 'listPrice'>) =>
  isVivaraYou && collectionId?.name ? (
    <ReleaseTag
      variant="product-card-vivara-you"
      name={`${collectionId?.name}`}
    />
  ) : (
    <>
      {skuPriceRange === null && (
        <span className={`${shelfClass}__installments`}>
          <Installment
            value={isProductAvailable ? spotPrice : price}
            formatter={useFormattedPrice}
            isKit={viewInstallments}
          />
        </span>
      )}
    </>
  )

export default function HandlePrice({
  spotPrice,
  listPrice,
  isProductAvailable,
  price,
  shelfClass,
  viewInstallments,
  isVivaraYou,
  collectionId,
  hasPolarizedLens,
  skuPriceRange = null,
}: HandlePriceType) {
  const isPrice = isProductAvailable ? spotPrice : price

  const formattedLowestPrice = useFormattedPrice(
    skuPriceRange?.lowestPrice ?? 0
  )

  const formattedHighestPrice = useFormattedPrice(
    skuPriceRange?.highestPrice ?? 0
  )

  return price === 0 ? (
    <BuyButton disabled data-card-buy-button="true">
      AVISE-ME
    </BuyButton>
  ) : (
    <>
      {skuPriceRange === null ? (
        <>
          <span className={`${shelfClass}__value-before`}>
            {listPrice !== isPrice && (
              <Price
                value={listPrice}
                formatter={useFormattedPrice}
                testId="price"
                data-value={listPrice}
                variant="listing"
                classes="text-body"
                SRText="Sale Price:"
              />
            )}
          </span>
          <span className={`${shelfClass}__value`}>
            <Price
              value={isProductAvailable ? spotPrice : price}
              formatter={useFormattedPrice}
              testId="price"
              data-value={isProductAvailable ? spotPrice : price}
              variant="spot"
              classes="text-body"
              SRText="Sale Price:"
            />
          </span>
        </>
      ) : (
        <span className={`${shelfClass}__value sku-price-range`}>
          {`${formattedLowestPrice} — ${formattedHighestPrice}`}
        </span>
      )}
      {!hasPolarizedLens ? (
        renderTagVivaraYouOrInstallments({
          isVivaraYou,
          collectionId,
          shelfClass,
          isProductAvailable,
          price,
          spotPrice,
          viewInstallments,
          skuPriceRange,
        })
      ) : (
        <div className={`${shelfClass}__installments`} />
      )}
    </>
  )
}
